import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  AlertCircle,
  CheckCircle,
  Mail,
  Trash2,
  ExternalLink,
  HardDrive,
  Activity
} from 'lucide-react';
import api from '../config/api';
import { Layout } from '../components/Layout';

export default function DomainMailboxes() {
  const { domainId } = useParams();
  const navigate = useNavigate();
  const [domain, setDomain] = useState(null);
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({ local_part: '', password: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchDomainAndMailboxes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId]);

  const fetchDomainAndMailboxes = async () => {
    try {
      setLoading(true);
      const [domainRes, mailboxesRes] = await Promise.all([
        api.get(`/api/domains/${domainId}`),
        api.get(`/api/domains/${domainId}/mailboxes`),
      ]);
      setDomain(domainRes.data.domain);
      setMailboxes(mailboxesRes.data.mailboxes);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Client-side validation
    const localPart = (formData.local_part || '').trim();
    if (!/^[a-z0-9._-]{1,64}$/.test(localPart)) {
      setError('Invalid local part. Use lowercase letters, numbers, dots, hyphens, and underscores (1-64 chars).');
      return;
    }
    if (!formData.password || formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    try {
      // Ensure local part is lowercase to match server expectations
      const payload = { ...formData, local_part: localPart.toLowerCase() };
      await api.post(`/api/domains/${domainId}/mailboxes`, payload);
      setSuccess('Mailbox created successfully');
      setFormData({ local_part: '', password: '' });
      setShowCreateForm(false);
      fetchDomainAndMailboxes();
    } catch (err) {
      // Prefer explicit error messages from the API
      const apiError = err.response?.data;
      if (apiError) {
        if (apiError.errors && Array.isArray(apiError.errors)) {
          setError(apiError.errors.map(e => e.msg).join('; '));
        } else if (apiError.error) {
          setError(apiError.error);
        } else {
          setError('Failed to create mailbox');
        }
      } else {
        setError('Failed to create mailbox');
      }
    }
  };

  const handleDelete = async (mailboxId) => {
    if (!window.confirm('Are you sure you want to delete this mailbox?')) return;

    try {
      await api.delete(`/api/domains/${domainId}/mailboxes/${mailboxId}`);
      setSuccess('Mailbox deleted successfully');
      fetchDomainAndMailboxes();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete mailbox');
    }
  };

  const toggleStatus = async (mailboxId, currentStatus) => {
    try {
      await api.put(`/api/domains/${domainId}/mailboxes/${mailboxId}`, {
        is_active: !currentStatus,
      });
      setSuccess('Mailbox status updated');
      fetchDomainAndMailboxes();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update mailbox');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-slate-500 font-medium">Loading mailboxes...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-8">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors"
          >
            <ArrowLeft size={16} className="mr-1" /> Back to Domains
          </Link>

          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                {domain?.domain_name}
              </h1>
              <p className="text-slate-500 mt-1">
                Manage email accounts and storage
              </p>
            </div>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className={`px-6 py-2.5 rounded-lg font-semibold shadow-sm transition-all flex items-center gap-2 ${showCreateForm
                  ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md'
                }`}
            >
              {showCreateForm ? 'Cancel' : (
                <>
                  <Plus size={18} /> Create Mailbox
                </>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg mb-6 text-sm flex items-center gap-2">
            <AlertCircle size={18} /> {error}
          </div>
        )}

        {success && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg mb-6 text-sm flex items-center gap-2">
            <CheckCircle size={18} /> {success}
          </div>
        )}

        {showCreateForm && (
          <div className="mb-8 bg-white rounded-xl shadow-sm border border-slate-200 p-6 animate-fade-in">
            <h2 className="text-lg font-bold text-slate-900 mb-4">New Mailbox</h2>
            <form onSubmit={handleCreate}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Email Address
                  </label>
                  <div className="flex items-center">
                    <input
                      type="text"
                      value={formData.local_part}
                      onChange={(e) =>
                        setFormData({ ...formData, local_part: e.target.value })
                      }
                      className="flex-1 border border-slate-200 rounded-l-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:z-10"
                      placeholder="username"
                      required
                    />
                    <span className="bg-slate-50 px-4 py-2.5 border border-l-0 border-slate-200 rounded-r-lg text-slate-500 font-medium">
                      @{domain?.domain_name}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5">
                    Only lowercase letters, numbers, dots, hyphens, and underscores
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Password
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    className="w-full border border-slate-200 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Min 8 characters"
                    required
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  className="bg-slate-900 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20"
                >
                  Create Mailbox
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {mailboxes.length === 0 ? (
            <div className="text-center py-16 px-6">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
                <Mail size={32} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">No mailboxes yet</h3>
              <p className="text-slate-500 max-w-sm mx-auto mb-6">
                Create your first email account to start sending and receiving emails.
              </p>
              <button
                onClick={() => setShowCreateForm(true)}
                className="text-blue-600 font-semibold hover:text-blue-700 flex items-center justify-center gap-1 mx-auto"
              >
                Create one now <ArrowLeft size={16} className="rotate-180" />
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Email</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Storage</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Last Login</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Created</th>
                    <th className="text-right px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {mailboxes.map((mailbox) => (
                    <tr key={mailbox.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 font-medium text-slate-900">{mailbox.email}</td>
                      <td className="px-6 py-4 text-slate-600">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-500 rounded-full"
                              style={{ width: `${Math.min((mailbox.storage_used_mb / 1024) * 100, 100)}%` }}
                            ></div>
                          </div>
                          <span className="text-xs">{mailbox.storage_used_mb} MB</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => toggleStatus(mailbox.id, mailbox.is_active)}
                          className={`px-2.5 py-1 rounded-full text-xs font-bold border transition-colors ${mailbox.is_active
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                            : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'
                            }`}
                        >
                          {mailbox.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500">
                        {mailbox.last_login
                          ? new Date(mailbox.last_login).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500">
                        {new Date(mailbox.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-3">
                          <button
                            onClick={() => navigate(`/webmail/${mailbox.id}`)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors flex items-center gap-1"
                          >
                            <ExternalLink size={14} /> Webmail
                          </button>
                          <button
                            onClick={() => handleDelete(mailbox.id)}
                            className="text-rose-600 hover:text-rose-800 text-sm font-medium transition-colors flex items-center gap-1"
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
              <Mail size={24} />
            </div>
            <div>
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Mailboxes Used</h3>
              <p className="text-2xl font-bold text-slate-900">
                {mailboxes.length} <span className="text-sm text-slate-400 font-normal">/ {domain?.mailbox_limit || 5}</span>
              </p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-purple-50 text-purple-600 rounded-lg">
              <HardDrive size={24} />
            </div>
            <div>
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Storage Quota</h3>
              <p className="text-2xl font-bold text-slate-900">
                {domain?.storage_quota_mb || 1024} <span className="text-sm text-slate-400 font-normal">MB / user</span>
              </p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg">
              <Activity size={24} />
            </div>
            <div>
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Domain Status</h3>
              <p className="text-2xl font-bold text-slate-900 capitalize">
                {domain?.status || 'Unknown'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
